import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield, BarChart3, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'belgo_cookie_consent_v1';

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function saveConsent(prefs) {
  const data = { ...prefs, essential: true, timestamp: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent('belgo-consent-changed', { detail: data }));
  // Consent Mode Integration
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push('consent', 'update', {
    'analytics_storage': data.analytics ? 'granted' : 'denied',
    'ad_storage': data.marketing ? 'granted' : 'denied',
    'ad_user_data': data.marketing ? 'granted' : 'denied',
    'ad_personalization': data.marketing ? 'granted' : 'denied'
  });

  // Custom event for GTM triggers
  window.dataLayer.push({
    event: 'consent_updated',
    consent_analytics: data.analytics,
    consent_marketing: data.marketing
  });

  return data;
}

export default function CookieConsent() {
  const { lang } = useLanguage();
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      setTimeout(() => setShow(true), 800);
    } else {
      setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing });
      
      // Push existing consent to dataLayer for returning visitors
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push('consent', 'update', {
        'analytics_storage': existing.analytics ? 'granted' : 'denied',
        'ad_storage': existing.marketing ? 'granted' : 'denied',
        'ad_user_data': existing.marketing ? 'granted' : 'denied',
        'ad_personalization': existing.marketing ? 'granted' : 'denied'
      });

      window.dataLayer.push({
        event: 'consent_updated',
        consent_analytics: !!existing.analytics,
        consent_marketing: !!existing.marketing
      });
    }
    const opener = () => { setShowSettings(true); setShow(true); };
    window.addEventListener('belgo-open-cookie-settings', opener);
    return () => window.removeEventListener('belgo-open-cookie-settings', opener);
  }, []);
  const acceptAll = () => { saveConsent({ analytics: true, marketing: true }); setShow(false); setShowSettings(false); };
  const rejectAll = () => { saveConsent({ analytics: false, marketing: false }); setShow(false); setShowSettings(false); };
  const savePrefs = () => { saveConsent(prefs); setShow(false); setShowSettings(false); };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border-2 border-[#f4d00d] overflow-hidden">
            {!showSettings ? (
              <div className="p-5 sm:p-6 flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#f4d00d] flex items-center justify-center shrink-0">
                    <Cookie className="w-5 h-5 text-black" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-bold text-[#1a1a1a] mb-1">
                      {lang === 'fr' ? 'Nous respectons votre vie privée' : 'We respect your privacy'}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#555555]">
                      {lang === 'fr'
                        ? 'Conformément à la Loi 25 du Québec, nous utilisons des témoins (cookies) pour améliorer votre expérience. Les témoins essentiels sont nécessaires au fonctionnement du site.'
                        : 'In accordance with Quebec\'s Law 25, we use cookies to improve your experience. Essential cookies are required for the site to function.'}{' '}
                      <Link to="/privacy" className="text-[#e53a3a] hover:underline font-semibold">
                        {lang === 'fr' ? 'En savoir plus' : 'Learn more'}
                      </Link>
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0 w-full lg:w-auto">
                  <button onClick={() => setShowSettings(true)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f0]" data-testid="cookie-customize">
                    {lang === 'fr' ? 'Personnaliser' : 'Customize'}
                  </button>
                  <button onClick={rejectAll} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f0]" data-testid="cookie-reject">
                    {lang === 'fr' ? 'Refuser' : 'Reject'}
                  </button>
                  <button onClick={acceptAll} className="btn-belgo px-5 py-2 rounded-lg text-sm font-bold text-white bg-[#e53a3a] hover:bg-[#c62828]" data-testid="cookie-accept-all">
                    {lang === 'fr' ? 'Tout accepter' : 'Accept all'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 sm:p-6 max-h-[80vh] overflow-y-auto" data-testid="cookie-settings">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold text-[#1a1a1a]">
                    {lang === 'fr' ? 'Préférences de témoins' : 'Cookie preferences'}
                  </h3>
                  <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-[#f5f5f0] rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex items-start gap-3 p-3 bg-[#f5f5f0] rounded-lg">
                    <Shield className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm text-[#1a1a1a]">{lang === 'fr' ? 'Essentiels' : 'Essential'}</p>
                        <span className="text-xs font-semibold text-green-700">{lang === 'fr' ? 'Toujours actifs' : 'Always on'}</span>
                      </div>
                      <p className="text-xs text-[#555555] mt-1">
                        {lang === 'fr' ? 'Nécessaires au fonctionnement du site (panier, session admin, accès au menu).' : 'Required for the site to function (cart, admin session, menu access).'}
                      </p>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 p-3 bg-white border border-[#e0e0e0] rounded-lg cursor-pointer hover:bg-[#f5f5f0]">
                    <BarChart3 className="w-5 h-5 text-[#e53a3a] shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm text-[#1a1a1a]">{lang === 'fr' ? 'Analytiques' : 'Analytics'}</p>
                        <input type="checkbox" checked={prefs.analytics} onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))} className="w-5 h-5 accent-[#e53a3a]" data-testid="cookie-analytics" />
                      </div>
                      <p className="text-xs text-[#555555] mt-1">
                        {lang === 'fr' ? 'Nous aident à comprendre comment vous utilisez le site pour l\'améliorer.' : 'Help us understand how you use the site to improve it.'}
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 bg-white border border-[#e0e0e0] rounded-lg cursor-pointer hover:bg-[#f5f5f0]">
                    <Megaphone className="w-5 h-5 text-[#e53a3a] shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm text-[#1a1a1a]">{lang === 'fr' ? 'Marketing' : 'Marketing'}</p>
                        <input type="checkbox" checked={prefs.marketing} onChange={e => setPrefs(p => ({ ...p, marketing: e.target.checked }))} className="w-5 h-5 accent-[#e53a3a]" data-testid="cookie-marketing" />
                      </div>
                      <p className="text-xs text-[#555555] mt-1">
                        {lang === 'fr' ? 'Personnalisation des publicités et offres promotionnelles.' : 'Personalized ads and promotional offers.'}
                      </p>
                    </div>
                  </label>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button onClick={rejectAll} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f0]" data-testid="cookie-reject-all-settings">
                    {lang === 'fr' ? 'Tout refuser' : 'Reject all'}
                  </button>
                  <button onClick={savePrefs} className="btn-belgo px-5 py-2 rounded-lg text-sm font-bold text-white bg-[#e53a3a] hover:bg-[#c62828]" data-testid="cookie-save-prefs">
                    {lang === 'fr' ? 'Enregistrer mes choix' : 'Save my choices'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('belgo-open-cookie-settings'));
}
